import { useState } from "react";
import { LockKeyhole, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { permissionLabel } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export function PermissionDenied({ permission, title = "You don't have permission" }) {
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const requestedLabel = permissionLabel(permission);

  async function handleRequest() {
    if (!permission || sent) return;
    setSending(true);
    try {
      await api.post("/permission-requests", { permission, note });
      setSent(true);
      toast.success("Permission request sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
          <LockKeyhole size={22} />
        </div>
        <h2 className="mt-4 text-lg font-bold text-slate-950">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Please contact your administrator and request this permission.
        </p>
        {permission && (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
            {requestedLabel}
          </div>
        )}
        {!sent ? (
          <div className="mt-4 space-y-3 text-left">
            <Textarea
              value={note}
              maxLength={50}
              onChange={(event) => setNote(event.target.value.slice(0, 50))}
              placeholder="Optional note"
              className="min-h-20 resize-none"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-slate-400">{note.length}/50</span>
              <Button onClick={handleRequest} disabled={sending || !permission} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Send size={14} /> {sending ? "Sending..." : "Request permission"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
            Request sent to your administrator.
          </div>
        )}
      </div>
    </div>
  );
}
